import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

export type T_FilterValueType = 'boolean' | 'select'

export type T_FilterValueOption = { label: string; value: string | boolean }

export type T_FilterCondition =
  | { acf_name: string; equals: boolean | string }
  | { acf_name: string; in: Array<boolean | string> }

export type T_Filter = {
  acf_name: string
  label: string
  question: string
  value_type: T_FilterValueType
  select_options?: T_FilterValueOption[]
  section_id: string
  show_in_guide: boolean
  applies_to: 'systems' | 'cases'
  short_description: string
  description: string
  show_if?: T_FilterCondition[]
}

export type T_AppliedFilter = T_Filter & {
  value: boolean | string
}

export type T_FilterSection = {
  id: string
  title: string
  icon: string
}

type FiltersState = {
  selectedFilter?: T_Filter
  setSelectedFilter: (filter: T_Filter | undefined) => void
  guideCompleted: boolean
  guideUsed: boolean
  setGuideUsed: (value: boolean) => void
  selectFirstGuideFilter: () => void
  selectNextGuideFilter: () => void
  selectPreviousGuideFilter: () => void
  appliedFilters: T_AppliedFilter[]
  addOrUpdateAppliedFilter: (filter: T_Filter, value: boolean | string) => void
  removeAppliedFilter: (acf_name: string) => void
  resetAppliedFilters: () => void
  filterSections: T_FilterSection[]
  filters: T_Filter[]
  getSelectedFilterSection: () => T_FilterSection | undefined
  getVisibleFilters: () => T_Filter[]
  getVisibleIndexOfSelected: () => number
  isSelectedFilterFirstVisible: () => boolean
}

// Conditional logic (visible filters) helpers

export const getAppliedValue = (applied: T_AppliedFilter[], key: string) =>
  applied.find((f) => f.acf_name === key)?.value

export const testCondition = (
  applied: T_AppliedFilter[],
  cond: T_FilterCondition
): boolean => {
  const v = getAppliedValue(applied, cond.acf_name)
  if ('equals' in cond) return v === cond.equals
  if ('in' in cond) return cond.in.includes(v as never)
  return false
}

export const isFilterVisible = (
  filter: T_Filter,
  applied: T_AppliedFilter[]
): boolean => {
  if (!filter.show_in_guide) return false
  if (!filter.show_if || filter.show_if.length === 0) return true
  // All conditions must pass (AND)
  return filter.show_if.every((c) => testCondition(applied, c))
}

export const useFiltersStore = create<FiltersState>()(
  devtools(
    persist(
      (set, get) => ({
        selectedFilter: undefined,

      setSelectedFilter: (filter) => set({ selectedFilter: filter }),

      guideCompleted: false,
      guideUsed: false,

      setGuideUsed: (value) => set({ guideUsed: value }),

      selectFirstGuideFilter: () => {
        const first = get().getVisibleFilters()[0]
        set({ selectedFilter: first, guideCompleted: false })
      },

      selectNextGuideFilter: () => {
        const list = get().getVisibleFilters()
        if (list.length === 0) return

        const idx = get().getVisibleIndexOfSelected()

        // No current or not found → go to first
        if (idx < 0) {
          set({ selectedFilter: list[0], guideCompleted: false })
          return
        }

        const next = list[idx + 1]

        if (next) {
          // move forward normally
          set({ selectedFilter: next })
        } else {
          // Last visible one → complete
          set({ guideCompleted: true })
        }
      },

      selectPreviousGuideFilter: () => {
        const list = get().getVisibleFilters()
        if (list.length === 0) return

        const idx = get().getVisibleIndexOfSelected()

        // If no current or already at first → stick to first and ensure not completed
        if (idx <= 0) {
          set({ selectedFilter: list[0], guideCompleted: false })
          return
        }

        const prev = list[idx - 1]
        set({ selectedFilter: prev, guideCompleted: false })
      },

      appliedFilters: [],

      addOrUpdateAppliedFilter: (filter, value) => {
        set((state) => {
          // Upsert
          const idx = state.appliedFilters.findIndex(
            (f) => f.acf_name === filter.acf_name
          )
          const nextApplied: T_AppliedFilter[] =
            idx === -1
              ? [...state.appliedFilters, { ...filter, value }]
              : state.appliedFilters.map((f, i) =>
                  i === idx ? { ...filter, value } : f
                )

          // Recompute visibility and drop applied values for now-hidden filters
          const nowVisible = state.filters.filter((f) =>
            isFilterVisible(f, nextApplied)
          )
          const visibleKeys = new Set(nowVisible.map((f) => f.acf_name))
          const prunedApplied = nextApplied.filter((f) =>
            visibleKeys.has(f.acf_name)
          )

          // If current selection is now hidden, jump forward
          let nextSelected = state.selectedFilter
          if (nextSelected && !visibleKeys.has(nextSelected.acf_name)) {
            const list = state.filters.filter((f) =>
              isFilterVisible(f, prunedApplied)
            )
            // pick the first still-visible after the changed one, else first, else undefined
            const afterIdx = list.findIndex(
              (f) => f.acf_name === filter.acf_name
            )
            nextSelected = list[afterIdx + 1] || list[0] || undefined
          }

          // If nothing visible remains, mark complete; else keep/reset completion
          const anyVisible = nowVisible.length > 0
          const guideCompleted = anyVisible ? state.guideCompleted : true

          return {
            appliedFilters: prunedApplied,
            selectedFilter: nextSelected,
            guideCompleted,
          }
        })
      },

      removeAppliedFilter: (acf_name) =>
        set((state) => ({
          appliedFilters: state.appliedFilters.filter(
            (f) => f.acf_name !== acf_name
          ),
        })),

      resetAppliedFilters: () => set({ appliedFilters: [] }),

      filterSections: [
        {
          id: 'prerequisites',
          title: 'Prerequisites',
          icon: 'fa-send-backward',
        },
        {
          id: 'site_and_resources',
          title: 'Site & Resources',
          icon: 'fa-earth-americas',
        },
        {
          id: 'energy_and_connections',
          title: 'Energy & Connections',
          icon: 'fa-pipe-valve',
        },
      ],

      filters: [
        // For cases ordering
        {
          acf_name: 'building_type',
          label: 'Building',
          question: 'What is the building type?',
          value_type: 'select',
          select_options: [
            { label: 'Retail', value: 'Retail' },
            { label: 'Office', value: 'Office' },
            { label: 'Factory', value: 'Factory' },
            { label: 'Warehouse', value: 'Warehouse' },
            { label: 'Hospitality', value: 'Hospitality' },
            {
              label: 'Arts, Community and Leisure',
              value: 'Arts, Community and Leisure',
            },
            { label: 'Education', value: 'Education' },
            { label: 'Health', value: 'Health' },
            { label: 'Emergency', value: 'Emergency' },
            { label: 'Transport', value: 'Transport' },
            { label: 'Other/unknown', value: 'Other/unknown' },
          ],
          section_id: 'prerequisites',
          show_in_guide: true,
          applies_to: 'cases',
          short_description: 'Short desc',
          description:
            '<p>In the case of mixed-use buildings, select the activity that represents the largest part of the area that will be served by the heat pump. If there is a change of use, select the new use</p><p>This question influences which case studies are displayed.</p>',
        },
        {
          acf_name: 'hvac_system_from',
          label: 'Previous system',
          question: 'What is the previous HVAC system?',
          value_type: 'select',
          select_options: [
            { label: 'Central hot water', value: 'Central hot water' },
            {
              label: 'Central hot water low temperature (<70 C)',
              value: 'Central hot water low temperature (<70 C)',
            },
            { label: 'Central air heating', value: 'Central air heating' },
            {
              label: 'Central air conditioning',
              value: 'Central air conditioning',
            },
            { label: 'Electric room heaters', value: 'Electric room heaters' },
            {
              label: 'Non-electric room heaters',
              value: 'Non-electric room heaters',
            },
            { label: 'Other/unknown', value: 'Other/unknown' },
          ],
          section_id: 'prerequisites',
          show_in_guide: true,
          applies_to: 'cases',
          short_description: 'Short desc',
          description:
            '<p>Please select the option that is closest to the actual system.</p><p>This question influences the short list and also which case studies are displayed.</p>',
        },
        {
          acf_name: 'floor_area',
          label: 'Floor area',
          question: 'What is the floor area?',
          value_type: 'select',
          select_options: [
            { label: '1000 m2 or less', value: '1000 m2 or less' },
            { label: '1000 - 5000 m2', value: '1000 - 5000 m2' },
            { label: '5000 m2 or more', value: '5000 m2 or more' },
            { label: 'Unknown', value: 'Unknown' },
          ],
          section_id: 'prerequisites',
          show_in_guide: true,
          applies_to: 'cases',
          short_description: 'Short desc',
          description:
            '<p>This question influences which case studies are displayed.</p>',
        },
        {
          acf_name: 'country',
          label: 'Country',
          question: 'In what country is the building situated?',
          value_type: 'select',
          select_options: [
            { label: 'Austria', value: 'Austria' },
            { label: 'Canada', value: 'Canada' },
            { label: 'Ireland', value: 'Ireland' },
            { label: 'Italy', value: 'Italy' },
            { label: 'Netherlands', value: 'Netherlands' },
            // { label: 'Sweden', value: 'Sweden' },
            { label: 'UK', value: 'UK' },
            { label: 'Other', value: 'Other' },
          ],
          section_id: 'prerequisites',
          show_in_guide: true,
          applies_to: 'cases',
          short_description: 'Short desc',
          description:
            '<p>If the country of interest is not listed, please choose one that has a similar climate or "other".</p><p>This question influences which case studies are displayed.</p>',
        },
        {
          acf_name: 'fabric_system_changes',
          label: 'Fabric/HVAC upgrade',
          question: 'How much alteration to the building fabric or HVAC system is anticipated?',
          value_type: 'select',
          select_options: [
            { label: 'Low', value: 'Low' },
            { label: 'Medium', value: 'Medium' },
            { label: 'High', value: 'High' },
            { label: 'Unknown', value: 'Unknown' },
          ],
          section_id: 'prerequisites',
          show_in_guide: true,
          applies_to: 'cases',
          short_description: 'Short desc',
          description:
            '<p>This question relates to changes that are not the direct consequence of the installation of a heat pump. This would include those resulting from a life-expired HVAC system or deterioration of the building fabric. Major refurbishment would be "high", minor changes such as limited window replacement changes such as window replacement would be "low", more extensive changes such as roof insulation or full replacement of windows would be "medium".</p><p>This question influences which case studies are displayed.</p>',
        },

        // For systems strikeout
        {
          acf_name: 'outdoor_space',
          label: 'Outdoor space',
          question:
            'Is there a suitable space outside or on the roof for the outdoor units of an air-source heat pump?',
          value_type: 'boolean',
          section_id: 'site_and_resources',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>This depends on the number and size of the heat pumps but as a rough indication, it might be a ground or roof area of 10 to 20 m2, or more for cold climate heat pumps. Reversible air to air heat pumps need smaller but, probably more numerous, outdoor space commonly on walls.</p>',
        },
        {
          acf_name: 'cold_climate',
          label: 'Cold climate',
          question: 'Is the building located in a very cold climate?',
          value_type: 'boolean',
          section_id: 'site_and_resources',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>Definitions vary but the term typically applies when the outdoor temperature is below -7C  and may be below -15C for extended periods.</p><p>Cold climate ASHPS are typically designed to operate down to -25C.</p>',
        },
        {
          acf_name: 'ground_space',
          label: 'Ground space',
          question:
            'Is there a suitable space for boreholes or ground trenches?',
          value_type: 'boolean',
          section_id: 'site_and_resources',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>This depends on the geology and the water table as well as the heat pump capacity but may be several hundred m2 for a borehole system and more for horizontal loops. Boreholes will be required for aquifer or ground-source systems. Typical locations include car parks or grassed areas.</p>',
        },
        {
          acf_name: 'aquifer_access',
          label: 'Aquifer access',
          question: 'Is there access to a suitable aquifer?',
          value_type: 'boolean',
          section_id: 'site_and_resources',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>This depends on the local geology.</p>',
        },
        {
          acf_name: 'water_access',
          label: 'Water access',
          question:
            'Is there a suitable open water source (river, lake or sea)?',
          value_type: 'boolean',
          section_id: 'site_and_resources',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>A body of water with stable year-round temperatures (typically over 4C), of sufficient flow or volume to support heat extraction.</p><p>These may be rivers canals, lakes, reservoirs, mines, aquifers, sewage flows, or the sea. There will be environmental requirements.</p>',
        },
        {
          acf_name: 'waste_heat_source',
          label: 'Waste heat source',
          question: 'Is there a nearby source of suitable waste heat?',
          value_type: 'boolean',
          section_id: 'site_and_resources',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>This could be from industrial processes, for example, but should be reliably available. Some air conditioning systems can use the reject heat from cooling as a heat source.</p>',
        },
        {
          acf_name: 'exhaust_air_source',
          label: 'Exhaust air source',
          question: 'Is there an accessible source of warm exhaust air?',
          value_type: 'boolean',
          section_id: 'site_and_resources',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>Exhaust air from mechanical ventilation can provide heat for a heat pump. For swimming pool ventilation, the latent heat of the water vapour can also be utilised.</p>',
        },
        {
          acf_name: 'electricity_supply',
          label: 'Electricity supply',
          question:
            'Is the electricity supply to the building sufficient for a heat pump system?',
          value_type: 'boolean',
          section_id: 'site_and_resources',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>If the addition of a heat pump would cause the demand to exceed the existing supply capacity, the supply will need to be upgraded.</p><p>Measured electricity consumption can identify the actual pre-retrofit peak. Hybrid systems may be used if it is impractical to increase the current supply capacity.</p>',
        },
        {
          acf_name: 'central_hydronic_system',
          label: 'Central hydronic system',
          question:
            'Is the heating system a centralised hydronic fossil fuelled system?',
          value_type: 'boolean',
          section_id: 'energy_and_connections',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>For example, gas, oil or biomass.</p><p>If the current heating system is electric, a heat pump will still save energy but the carbon savings will depend on the local electricity generation system.</p>',
        },
        {
          acf_name: 'ducted_air_system',
          label: 'Ducted air system',
          question:
            'Does the heating subsystem of the air conditioning use ducted warm air?',
          value_type: 'boolean',
          section_id: 'energy_and_connections',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>This will influence the choice of options.</p>',
	        show_if: [{ acf_name: 'central_hydronic_system', equals: false }],
        },
        {
          acf_name: 'room_units',
          label: 'Room units',
          question:
            'Is heating provided by individual room units that use fossil fuels or electricity other than heat pumps?',
          value_type: 'boolean',
          section_id: 'energy_and_connections',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>This will influence the choice of options.</p>',
	        show_if: [{ acf_name: 'ducted_air_system', equals: false }],
        },
        {
          acf_name: 'hydronic_subsystem',
          label: 'Hydronic subsystem',
          question: 'If the system includes an air handling unit, is the heat to it provided by a hydronic subsystem?',
          value_type: 'boolean',
          section_id: 'energy_and_connections',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>This will influence the choice of options.</p>',
	        show_if: [{ acf_name: 'ducted_air_system', equals: true }],
        },
        {
          acf_name: 'air_conditioning',
          label: 'Air conditioning',
          question: 'Does the building have or require cooling?',
          value_type: 'boolean',
          section_id: 'energy_and_connections',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>If so, some of the options will be able to use of the heat rejected for heating.</p>',
        },
        {
          acf_name: 'water_chiller',
          label: 'Water chiller',
          question: 'Does the air conditioning system include a water chiller?',
          value_type: 'boolean',
          section_id: 'energy_and_connections',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>If so, replacement of the chiller by a heat pump becomes an option.</p>',
          show_if: [{ acf_name: 'air_conditioning', equals: true }],
          // Example, we can also do multi-option conditions
          // show_if: [{ acf_name: 'cold_climate', in: [true, 'high'] }]
        },
        {
          acf_name: 'radiant_system',
          label: 'Radiant system',
          question:
            'Is the heating systems a fossil fuelled high temperature radiant system?',
          value_type: 'boolean',
          section_id: 'energy_and_connections',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>This will require a complete system replacement.</p>',
        },
        {
          acf_name: 'steam_system',
          label: 'Steam system',
          question: 'Is the heating system a steam system?',
          value_type: 'boolean',
          section_id: 'energy_and_connections',
          show_in_guide: true,
          applies_to: 'systems',
          short_description: 'Short desc',
          description:
            '<p>This will probably require a complete system replacement.</p>',
        },
      ],

      getSelectedFilterSection: () => {
        const { selectedFilter, filterSections } = get()
        if (!selectedFilter) return undefined
        return filterSections.find(
          (section) => section.id === selectedFilter.section_id
        )
      },

      getVisibleFilters: () => {
        const { filters, appliedFilters } = get()
        return filters.filter((f) => isFilterVisible(f, appliedFilters))
      },

      getVisibleIndexOfSelected: () => {
        const { selectedFilter } = get()
        if (!selectedFilter) return -1
        const list = get().getVisibleFilters()
        return list.findIndex((f) => f.acf_name === selectedFilter.acf_name)
      },

      isSelectedFilterFirstVisible: () => {
        const idx = get().getVisibleIndexOfSelected()
        return idx === 0
      },
      }),
      {
        name: 'filters-store',
        partialize: (state) => ({
          appliedFilters: state.appliedFilters,
          selectedFilter: state.selectedFilter,
          guideUsed: state.guideUsed,
          guideCompleted: state.guideCompleted,
        }),
      }
    ),
    { name: 'FiltersStore' } // nice to have for Redux DevTools tab naming
  )
)
